import { customerLabel, searchCustomers } from '../calc/customers.js';
import { div, esc, fmtDate, kr, nf1, num, pct } from '../calc/format.js';
import { CFG } from '../calc/model.js';
import { daysBetween } from '../calc/periods.js';
import { NONE } from '../calc/selection.js';
import { el, emptyRow, headCell, sortRows } from './dom.js';
import { downloadXlsx, periodSlug } from './export.js';
import { helpButton } from './help.js';
import { state } from './state.js';

/* --------------------------------------------------------------------------
   Kunder och bonuscheckar

   Fliken visas när filen har kvitton med kund (state.hasCustomers). Allt
   räknas på urvalet, som övriga flikar: väljs en enda kund i kundurvalet
   visar varje flik bara den kundens köp, och här visas en kundprofil.

   Bonuscheckar gäller hela kvittot och nollas i ett radurval (artikelgrupp
   eller leverantör), precis som betalningarna.
   -------------------------------------------------------------------------- */

const WEEKDAYS = ['måndag','tisdag','onsdag','torsdag','fredag','lördag','söndag'];

/** Hur länge före filens sista dag kunden senast handlade. Räknas från filen, inte från idag. */
function since(day){
  const n = daysBetween(day, state.bounds.max) - 1;
  return n <= 0 ? 'filens sista dag' : n === 1 ? 'dagen före filens sista dag' : `${num(n)} dagar före filens sista dag`;
}

/** Sant när urvalet har kunder valda, även "Utan kund". */
export const customerFiltered = () => (state.scopeSelects.customer?.value || []).length > 0;

/** Kunden som är vald i urvalet, när det är exakt en, annars null. */
export function selectedCustomer(){
  const v = state.scopeSelects.customer?.value || [];
  return v.length === 1 && v[0] !== NONE ? v[0] : null;
}

export function renderCustomers(){
  if(!state.hasCustomers) return;
  const s = state.stats;
  const has = state.view.length > 0;

  const set = (id, v, m) => { el(id).textContent = v; el(id + 'M').textContent = m; };
  const anyCust = has && s.custTx > 0;
  set('kCustShare', has ? pct(s.custShare) : '–', has ? `${num(s.custTx)} av ${num(s.tx)} köp` : 'Inga köp i urvalet');
  set('kCustRev', anyCust ? pct(s.custRevenueShare) : '–', anyCust ? kr(s.custRevenue) : 'Inga köp med kund');
  set('kCustAov', anyCust ? kr(s.custAov) : '–',
      !has ? '–' : s.otherTx ? `Utan kund ${kr(s.otherAov)}` : 'Alla köp har kund');
  set('kCustCount', anyCust ? num(s.customerCount) : '–',
      anyCust ? `${num(s.returningCustomers)} har handlat mer än en gång` : 'Inga köp med kund');
  set('kCustFreq', anyCust ? nf1.format(s.txPerCustomer).replace('.', ',') : '–', anyCust ? 'I vald period' : '–');
  if(state.lineFiltered) set('kBonus', '–', 'Gäller hela kvitton');
  else set('kBonus', has ? num(s.bonusCount) : '–', has && s.bonusCount ? kr(s.bonusAmount) : 'Inga inlösta i urvalet');

  renderProfile(s, has);
  renderBonus(s, has);
  renderCustomerTable(s, has);
}

function renderProfile(s, has){
  const nr = selectedCustomer();
  el('profileCard').hidden = !nr;
  if(!nr) return;
  const name = state.customerNames.get(nr) || '';
  el('profileTitle').textContent = customerLabel(nr, name);
  el('profileSub').textContent = 'Köp i vald period. Övriga flikar visar också bara den här kunden.';
  const c = s.customers.get(nr);
  if(!has || !c){
    el('tProfile').innerHTML = emptyRow(2, 'Kunden handlade inte i urvalet', 'Välj en längre period eller ta bort andra val i urvalet.');
    return;
  }

  // Vanligaste veckodag och timme, räknat i antal köp
  const top = map => [...map.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0];
  const wd = top(new Map([...s.byWeekday].map(([k, v]) => [k, v.tx])));
  const hr = top(s.byHourTx);
  const favourites = [...s.products.values()].filter(p => p.receipts > 0)
    .sort((a, b) => b.receipts - a.receipts || b.revenue - a.revenue).slice(0, 5)
    .map(p => `${p.name} (${num(p.receipts)} köp)`);

  const rows = [
    ['Köp', `${num(c.tx)} · ${kr(c.revenue)}`],
    ['Snittköp', kr(div(c.revenue, c.tx))],
    ['Artiklar per köp', nf1.format(div(c.units, c.tx)).replace('.', ',')],
    ['Första köp', fmtDate(c.first)],
    ['Senaste köp', `${fmtDate(c.last)} · ${since(c.last)}`],
    ['Handlar oftast', wd && hr ? `${WEEKDAYS[wd[0]]}ar, kl. ${String(hr[0]).padStart(2, '0')}–${String(hr[0] + 1).padStart(2, '0')}` : '–'],
    ['Köper oftast', favourites.length ? favourites.join(', ') : '–'],
    ['Rabatt', c.discount ? kr(c.discount) : '–'],
    ['Inlösta bonuscheckar', state.lineFiltered ? 'Gäller hela kvitton' : c.bonusCount ? `${num(c.bonusCount)} · ${kr(c.bonus)}` : '–']
  ];
  el('tProfile').innerHTML = '<tbody>' + rows.map(([k, v]) =>
    `<tr><th scope="row" class="kv">${esc(k)}</th><td>${esc(v)}</td></tr>`).join('') + '</tbody>';
}

function renderBonus(s, has){
  el('bonusCard').hidden = !state.hasBonus;
  if(!state.hasBonus) return;
  el('bonusHelp').innerHTML = helpButton('bonuscheck');
  const t = el('tBonus');
  if(state.lineFiltered){
    t.innerHTML = emptyRow(2, 'Bonuscheckar gäller hela kvitton',
      'De visas inte när artikelgrupp eller leverantör är vald, precis som betalsätten.');
    return;
  }
  if(!has || !s.bonusCount){
    t.innerHTML = emptyRow(2, 'Inga bonuscheckar lösta i urvalet', 'Välj en längre period eller ett bredare urval.');
    return;
  }
  const rows = [
    ['Inlösta bonuscheckar', `${num(s.bonusCount)} · ${kr(s.bonusAmount)}`],
    ['Kunder som löste in', s.customerCount ? `${num(s.bonusCustomers)} av ${num(s.customerCount)} kunder` : '–'],
    ['Andel av försäljningen med kund', s.custRevenue > 0 ? pct(s.bonusShareOfCustRevenue) : '–'],
    ['Betalt utöver checken, i snitt', kr(s.bonusPaidAvg)],
    ['Köp som checken betalade helt', `${num(s.bonusFullCover)} av ${num(s.bonusTx)}`]
  ];
  if(s.bonusNoCustomer) rows.push(['Inlösta på kvitton utan kund', num(s.bonusNoCustomer)]);
  t.innerHTML = '<tbody>' + rows.map(([k, v]) =>
    `<tr><th scope="row" class="kv">${esc(k)}</th><td class="num">${esc(v)}</td></tr>`).join('') + '</tbody>';
}

/** Kundtabellens rader: en per kund i urvalet. */
export function customerRows(s){
  return [...s.customers.values()].map(c => ({
    number: c.number, name: c.name, label: customerLabel(c.number, c.name),
    tx: c.tx, revenue: c.revenue, aov: div(c.revenue, c.tx), discount: c.discount,
    bonusCount: c.bonusCount, bonus: c.bonus, first: c.first, last: c.last
  }));
}

function renderCustomerTable(s, has){
  const t = el('tCustomers');
  if(!has || !s.customers.size){
    el('cCustomer').textContent = '';
    t.innerHTML = emptyRow(8, has ? 'Inga köp med kund i urvalet' : 'Inga kvitton i urvalet',
      'Välj en annan period eller ett bredare urval.');
    return;
  }
  let rows = searchCustomers(customerRows(s), state.customerQuery);
  const total = rows.length;
  rows = sortRows(rows, state.customerSort).slice(0, CFG.MAX_ROWS);
  el('cCustomer').textContent = total > CFG.MAX_ROWS
    ? `Visar ${num(CFG.MAX_ROWS)} av ${num(total)} kunder`
    : `${num(total)} ${total === 1 ? 'kund' : 'kunder'}`;
  if(!total){ t.innerHTML = emptyRow(8, 'Ingen kund matchar sökningen', 'Prova ett annat namn eller kundnummer.'); return; }

  const S = state.customerSort;
  const chosen = selectedCustomer();
  let html = '<thead><tr>' +
    headCell('Kund', 'name', S) +
    headCell('Köp', 'tx', S, 'num') +
    headCell('Omsättning', 'revenue', S, 'num') +
    headCell('Snittköp', 'aov', S, 'num') +
    headCell('Rabatt', 'discount', S, 'num') +
    headCell('Bonuscheckar', 'bonusCount', S, 'num') +
    headCell('Senaste köp', 'last', S) +
    '<th><span class="sr-only">Visa kunden</span></th></tr></thead><tbody>';
  for(const r of rows){
    html += `<tr>
      <td class="name">${esc(r.name || 'Namn saknas')}<small>Kundnummer ${esc(r.number)}</small></td>
      <td class="num">${esc(num(r.tx))}</td>
      <td class="num">${esc(kr(r.revenue))}</td>
      <td class="num">${esc(kr(r.aov))}</td>
      <td class="num">${r.discount ? esc(kr(r.discount)) : '–'}</td>
      <td class="num">${state.lineFiltered ? '–' : r.bonusCount ? esc(`${num(r.bonusCount)} · ${kr(r.bonus)}`) : '–'}</td>
      <td>${esc(fmtDate(r.last))}</td>
      <td>${r.number === chosen ? '<span class="count">Vald</span>'
        : `<button type="button" class="btn btn-ghost btn-sm" data-customer="${esc(r.number)}" aria-label="Visa bara ${esc(r.label)}">Visa</button>`}</td>
    </tr>`;
  }
  t.innerHTML = html + '</tbody>';
}

/** Kundlistan som den visas, med sökningen. */
export function exportCustomers(){
  const s = state.stats;
  const rows = searchCustomers(customerRows(s), state.customerQuery)
    .sort((a, b) => b.revenue - a.revenue)
    .map(c => [c.number, c.name, c.tx, c.revenue, c.aov, c.discount,
               state.lineFiltered ? null : c.bonusCount, state.lineFiltered ? null : c.bonus,
               c.first, c.last]);
  return downloadXlsx(`easycashier-kunder-${periodSlug()}${state.customerQuery.trim() ? '-sokning' : ''}`, {
    sheet: 'Kunder',
    columns: [{h:'Kundnummer'}, {h:'Namn'}, {h:'Köp', t:'int'}, {h:'Omsättning inkl. moms', t:'num'},
      {h:'Snittköp', t:'num'}, {h:'Rabatt', t:'num'}, {h:'Inlösta bonuscheckar', t:'int'},
      {h:'Bonuscheckar (kr)', t:'num'}, {h:'Första köp', t:'date'}, {h:'Senaste köp', t:'date'}],
    rows
  });
}
